import React, {Component} from 'react';
import ReactDOM from "react-dom";

/**
 * Component to display a modal.
 *
 * Supported props:
 *  id - The id to apply to the modal's div
 *  title - The title to display in the modal's header
 *  confirmAction - The text to display for the confirm/ok/save button
 *  onSave - The function to execute when the confirm button is clicked
 *  cancelAction - The text to display for the cancel/close button
 *  onCancel - The function to execute when the cancel button is clicked.
 *  modalHidden - The function to execute when the modal closes.
 *  children - JSX element to display as the modal's body.
 *  footerActionEl - JSX element to display in the footer.
 *  doConfirm - A method to call to check if the modal can be closed without using an action button (Clicking outside or 'x')
 *  hideCloseX - A flag to hide the 'x' close button.
 *  modalLarge - A flag to add the 'modal-lg' class to make the modal wider
 *  displayProps - Optional properties to customize the modal elements
 */
class Modal extends Component {
	/**
	 * Show the modal when the component mounts.
	 */
	componentDidMount() {
		const modalElement = ReactDOM.findDOMNode(this);
		modalElement.addEventListener('hidden.bs.modal', this.props.modalHidden);
		window.bootstrap.Modal.getOrCreateInstance(modalElement).show();

		// Call closeModal when Bootstrap fires the hide event
		// The hide event happens when clicking out of the modal or when closing the modal from a button click		
		const closeModal = this.closeModal.bind(this);
		// Keep a reference to the event handler so we can remove it when the component unmounts
		this.closeModalHandler = function (e) {
			closeModal(true, e);
		};
		modalElement.addEventListener('hide.bs.modal', this.closeModalHandler);

		// bootstrap puts a blur on ".modal-open .page-wrapper" and recommends placing modals in a top-level to prevent
		// other CSS from affecting the modal appearance. We can't do this though, because the react root is nested
		// pretty deep in the wrapping DOM, so instead we're programatically dropping the blurry styles
		window.$(".page-wrapper").css({"filter": "none", "-webkit-filter": "none", "-moz-filter": "none", "-o-filter": "none", "-ms-filter": "none"});
	}

	componentWillUnmount() {
		this.closeModal();
	}

	handleSave = () => {
		if (this.props.onSave) {
			this.props.onSave();
		}
		this.closeModal();
	}

	handleCancel = () => {
		if (this.props.onCancel) {
			this.props.onCancel();
		}
		this.closeModal();
	}

	/**
	 * Handles a modal close event.
	 * 
	 * If confirmation is required the props.doConfirm method is called and the modal closes
	 * based on the result of that function.
	 * If confirmation is not required, turn off the 'hide.bs.modal' event so we don't get stuck
	 * in a loop, calling this method continuously, and hide the model.
	 * 
	 * @param confirm A flag indicating if confirmation is needed before closing the modal
	 * @param e The event that triggered the modal to close
	 */
	closeModal = (confirm, e) => {
		// check if the form data was changed since the modal was openened
		if (confirm && this.props.doConfirm) {
			this.props.doConfirm(e);
		} else {
			const modalElement = ReactDOM.findDOMNode(this);
			modalElement.removeEventListener('hide.bs.modal', this.closeModalHandler); // Remove the 'hide.bs.modal' event handler
			window.bootstrap.Modal.getInstance(modalElement).hide();
		}
	}

	/**
	 * Renders the modal component.
	 */
	render() {
		let modalSizeClass = '';
		if (this.props.modalXL) {
			modalSizeClass = ' modal-xl';
		} else if (this.props.modalLarge) {
			modalSizeClass = ' modal-lg';
		}
		return (<div className="modal fade" id={this.props.id || 'genericModal'} tabIndex="-1" role="dialog">
			<div className={"modal-dialog" + modalSizeClass} role="document">
				<div className="modal-content" {...this.props.displayProps?.modalContent}>
					<div className="modal-header" {...this.props.displayProps?.modalHeader}>
						{(!this.props.hideCloseX) && (
							<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						)}
						<h4 className="modal-title">{this.props.title}</h4>
					</div>
					<div className="modal-body" {...this.props.displayProps?.modalBody}>
						{this.props.children}
					</div>
					{(this.props.footerActionEl || this.props.cancelAction || this.props.confirmAction) && (
						<div className="modal-footer">
							{this.props.footerActionEl}
							{this.props.cancelAction && <button type="button" className="btn btn-light" onClick={this.handleCancel}>{this.props.cancelAction}</button>}
							{this.props.confirmAction && <button type="button" className="btn btn-primary" disabled={this.props.disableConfirm} onClick={this.handleSave}>{this.props.confirmAction}</button>}
						</div>
					)}
				</div>
			</div>
		</div>);
	};
}

/**
 * Properties to style the modal and modify behavior in a common, but non-standard way.
 * All template behavior can be overridden by passing in the appropriate prop.
 * To display a BlankRoundedCentered modal you could use `<Modal {...modalTemplates.BlankRoundedCentered}>`
 */
const modalTemplates = {
	BlankRoundedCentered: {
		displayProps: {
			modalHeader: { style: { display: 'none' } }, // Hide the modal-header
			modalContent: { style: { borderRadius: '20px' } }, // Round corners
			modalBody: { className: 'modal-body text-center' } // Center text
		},
		doConfirm: (event) => { // Disable close when clicked outside or pressing Escape
			event && event.preventDefault();
			event && event.stopPropagation();
			return false;
		}
	}
};

export { modalTemplates }

export default Modal;

